import { useEffect, useRef, useState } from "react";
import {
  useClientProjectsStore,
  type RequestAttachment,
} from "../../../store/useClientProjectsStore";
import { MIcon, ModalShell } from "./shared";

/**
 * Start New Project.
 *
 * This submits a REQUEST, not a project. Nothing here creates anything
 * operational — the team reviews what the client asks for and converts it
 * afterwards. The copy says so plainly, because a client who thinks they have
 * started a project and then sees nothing in their Projects list has been
 * misled by the UI.
 *
 * The three required fields are validated here for the client's convenience and
 * again on the server, which is where the rule actually lives.
 */

const MAX_FILES = 10;
const MAX_FILE_MB = 25;
const ACCEPT = ".jpg,.jpeg,.png,.pdf,.doc,.docx";

const Field = ({
  label,
  required,
  hint,
  error,
  children,
}: {
  label: string;
  // exactOptionalPropertyTypes is on, and errors[key] is genuinely
  // string | undefined, so these have to admit undefined explicitly.
  required?: boolean | undefined;
  hint?: string | undefined;
  error?: string | undefined;
  children: React.ReactNode;
}) => (
  <label className="block mb-md">
    <span className="block text-[11px] font-bold uppercase tracking-wider text-tertiary mb-1">
      {label}
      {required && <span className="text-error ml-0.5">*</span>}
    </span>
    {children}
    {error ? (
      <span className="block text-[11px] font-semibold text-error mt-1">{error}</span>
    ) : hint ? (
      <span className="block text-[11px] text-on-surface-variant mt-1">{hint}</span>
    ) : null}
  </label>
);

const inputClass =
  "w-full px-3 py-2 bg-surface-container-lowest border border-outline-variant rounded-lg text-body-sm text-on-surface outline-none focus:border-primary transition-colors";

const NewProjectRequestModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const categories = useClientProjectsStore((s) => s.requestCategories);
  const fetchRequestOptions = useClientProjectsStore((s) => s.fetchRequestOptions);
  const uploadRequestFiles = useClientProjectsStore((s) => s.uploadRequestFiles);
  const submitProjectRequest = useClientProjectsStore((s) => s.submitProjectRequest);

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [requirements, setRequirements] = useState("");
  const [extras, setExtras] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [files, setFiles] = useState<RequestAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    if (!categories.length) fetchRequestOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const reset = () => {
    setTitle("");
    setCategory("");
    setRequirements("");
    setExtras("");
    setDeliveryDate("");
    setFiles([]);
    setErrors({});
  };

  const close = () => {
    if (submitting || uploading) return;
    reset();
    onClose();
  };

  const validate = () => {
    const next: Record<string, string> = {};
    if (!title.trim()) next.title = "Give your project a name.";
    if (!category) next.category = "Choose the type of work you need.";
    if (!requirements.trim()) next.requirements = "Tell the team what you need.";
    else if (requirements.trim().length < 20) {
      next.requirements = "A little more detail, please — enough for the team to understand the work.";
    }
    if (deliveryDate) {
      const d = new Date(deliveryDate);
      d.setHours(23, 59, 59, 999);
      if (d.getTime() < Date.now()) next.deliveryDate = "That date has already passed.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onPickFiles = async (picked: FileList | null) => {
    if (!picked?.length) return;
    const chosen = [...picked];

    if (files.length + chosen.length > MAX_FILES) {
      setErrors((e) => ({ ...e, files: `You can attach up to ${MAX_FILES} files.` }));
      return;
    }
    const tooBig = chosen.find((f) => f.size > MAX_FILE_MB * 1024 * 1024);
    if (tooBig) {
      setErrors((e) => ({ ...e, files: `${tooBig.name} is larger than ${MAX_FILE_MB}MB.` }));
      return;
    }

    setErrors((e) => ({ ...e, files: "" }));
    setUploading(true);
    const uploaded = await uploadRequestFiles(chosen);
    setUploading(false);
    if (uploaded.length) setFiles((f) => [...f, ...uploaded]);
    if (fileInput.current) fileInput.current.value = "";
  };

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    const ok = await submitProjectRequest({
      projectTitle: title.trim(),
      category,
      requirements: requirements.trim(),
      additionalRequirements: extras.trim(),
      ...(deliveryDate ? { expectedDeliveryDate: deliveryDate } : {}),
      documents: files,
    });
    setSubmitting(false);
    if (ok) {
      reset();
      onClose();
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <ModalShell
      open={open}
      onClose={close}
      icon="add_circle"
      title="Start a New Project"
      subtitle="Tell us what you need and the team will review it with you"
      width="max-w-2xl"
    >
      {/* Said up front, not buried in a confirmation afterwards. */}
      <div className="flex items-start gap-2.5 p-md mb-lg rounded-xl bg-primary/5 border border-primary/20">
        <MIcon name="info" className="text-primary text-[18px] mt-0.5" />
        <p className="text-body-sm text-on-surface leading-relaxed">
          This sends a request, not a booking. The team will review it, come back to you with
          anything they need, and set the project up once everything is agreed.
        </p>
      </div>

      <Field label="Project name" required error={errors.title}>
        <input
          className={inputClass}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Brand identity for our new product line"
          maxLength={140}
        />
      </Field>

      <Field label="Category" required error={errors.category} hint="The kind of work you need">
        <select
          className={inputClass}
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          <option value="">Select a category…</option>
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </Field>

      {!categories.length && (
        <p className="text-[11px] text-on-surface-variant -mt-sm mb-md">
          Categories could not be loaded. Please refresh, or send us a message instead.
        </p>
      )}

      <Field
        label="What do you need?"
        required
        error={errors.requirements}
        hint="What you want, who it is for, and anything that matters to you"
      >
        <textarea
          className={`${inputClass} min-h-[120px] resize-y`}
          value={requirements}
          onChange={(e) => setRequirements(e.target.value)}
          placeholder="Describe the work you have in mind…"
          maxLength={5000}
        />
      </Field>

      <Field
        label="Expected delivery date"
        error={errors.deliveryDate}
        hint="Optional — tells the team what you are working towards"
      >
        <input
          type="date"
          className={inputClass}
          value={deliveryDate}
          min={today}
          onChange={(e) => setDeliveryDate(e.target.value)}
        />
      </Field>

      <Field
        label="Anything else"
        hint="Optional — references, brand guidelines, constraints, budget range"
      >
        <textarea
          className={`${inputClass} min-h-[72px] resize-y`}
          value={extras}
          onChange={(e) => setExtras(e.target.value)}
          placeholder="Optional"
          maxLength={3000}
        />
      </Field>

      <Field
        label="Attachments"
        error={errors.files}
        hint={`Up to ${MAX_FILES} files · JPG, PNG, PDF or Word · ${MAX_FILE_MB}MB each`}
      >
        <input
          ref={fileInput}
          type="file"
          multiple
          accept={ACCEPT}
          onChange={(e) => onPickFiles(e.target.files)}
          disabled={uploading || files.length >= MAX_FILES}
          className="block w-full text-body-sm text-on-surface-variant file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-body-sm file:font-semibold file:bg-surface-container file:text-on-surface hover:file:bg-surface-container-high file:cursor-pointer disabled:opacity-50"
        />
      </Field>

      {uploading && (
        <p className="text-body-sm text-on-surface-variant -mt-sm mb-md">Uploading…</p>
      )}

      {files.length > 0 && (
        <ul className="space-y-1.5 -mt-sm mb-lg">
          {files.map((f, i) => (
            <li
              key={i}
              className="flex items-center justify-between gap-2 text-body-sm p-2.5 rounded-lg bg-surface-container-low"
            >
              <span className="flex items-center gap-2 min-w-0">
                <MIcon name="attach_file" className="text-on-surface-variant text-[16px]" />
                <span className="text-on-surface truncate">{f.name}</span>
              </span>
              <button
                type="button"
                onClick={() => setFiles((list) => list.filter((_, idx) => idx !== i))}
                className="text-on-surface-variant hover:text-error transition-colors flex-shrink-0"
                aria-label={`Remove ${f.name}`}
              >
                <MIcon name="close" className="text-[16px]" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center justify-end gap-sm pt-md border-t border-outline-variant">
        <button
          type="button"
          onClick={close}
          disabled={submitting || uploading}
          className="px-lg py-2 rounded-lg border border-outline-variant text-body-sm font-semibold text-on-surface hover:bg-surface-container-low disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={submitting || uploading}
          className="px-lg py-2 rounded-lg bg-primary text-on-primary text-body-sm font-semibold hover:bg-primary/90 disabled:opacity-50 shadow-sm"
        >
          {submitting ? "Sending…" : "Submit Request"}
        </button>
      </div>
    </ModalShell>
  );
};

export default NewProjectRequestModal;
